import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCVContext } from '@/context/CVContext';
import { useLanguage } from '@/context/LanguageContext';
import Header from '@/components/Header';
import OpenAppButton from '@/components/OpenAppButton';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Download, ArrowLeft, Mail, Phone, MapPin, Calendar, Building2, GraduationCap,
  Briefcase, Code, Languages as LanguagesIcon, Award, Brain, CheckCircle,
  Linkedin, Sparkles, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { downloadCompleteProfilePdf } from '@/services/pdfService';
import { useDownloadGate } from '@/hooks/useDownloadGate';
import DownloadGateModal from '@/components/DownloadGateModal';

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { state, dispatch } = useCVContext();
  const [profile, setProfile] = useState(state?.profile || null);
  const { language } = useLanguage();
  const documentRef = useRef(null);
  const [personalityResult, setPersonalityResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // Download gate hook
  const {
    showModal: showDownloadGate,
    initiateDownload,
    onLeadCaptured,
    closeModal: closeDownloadGate,
    downloadType,
    isChecking: isCheckingLead
  } = useDownloadGate(profile?.id);

  const isEnglish = language === 'en';

  useEffect(() => {
    // Try to get profile from context first, then localStorage
    let currentProfile = state?.profile;
    
    if (!currentProfile?.id) {
      const savedProfile = localStorage.getItem('cvProfile');
      if (savedProfile) {
        try {
          currentProfile = JSON.parse(savedProfile);
          if (currentProfile?.id) {
            // Set profile in context
            dispatch({ type: 'SET_PROFILE', payload: currentProfile });
            setProfile(currentProfile);
          }
        } catch (e) {
          console.error('Error parsing saved profile:', e);
        }
      }
    } else {
      setProfile(currentProfile);
    }

    if (!currentProfile?.id) {
      navigate('/');
      return;
    }

    const fetchPersonality = async () => {
      try {
        const saved = localStorage.getItem('personalityResult');
        if (saved) {
          const parsed = JSON.parse(saved);
          setPersonalityResult(parsed.result);
          setLoading(false);
          return;
        }

        const response = await api.get(`/personality/result/${currentProfile.id}`);
        if (response.data.success) {
          setPersonalityResult(response.data.result);
        }
      } catch (error) {
        console.log('No personality result found for this profile');
      } finally {
        setLoading(false);
      }
    };

    fetchPersonality();
  }, [state?.profile, navigate, dispatch]);

  // Actual PDF download function - uses server-side generation
  const executeDownloadPDF = async () => {
    if (!profile?.id) {
      toast.error(isEnglish ? 'Error: Profile not found' : 'Erreur: Profil non trouvé');
      return;
    }
    setDownloading(true);
    toast.loading(isEnglish ? 'Generating PDF...' : 'Génération du PDF...', { id: 'pdf-loading' });

    try {
      // Use server-side PDF generation for professional quality
      await downloadCompleteProfilePdf(
        profile.id,
        isEnglish ? 'en' : 'fr',
        profile?.first_name || 'Profile',
        profile?.last_name || ''
      );
      
      toast.dismiss('pdf-loading');
      toast.success(isEnglish ? 'PDF downloaded!' : 'PDF téléchargé !');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.dismiss('pdf-loading');
      toast.error(isEnglish ? 'Error generating PDF' : 'Erreur lors de la génération du PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Handler that goes through download gate
  const handleDownloadPDF = async () => {
    await initiateDownload(executeDownloadPDF, 'complete_profile_pdf');
  };

  const shareOnLinkedIn = () => {
    window.open('https://www.linkedin.com/sharing/share-offsite/', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#5BA946] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{isEnglish ? 'Loading...' : 'Chargement...'}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
        <div className="text-center">
          <p className="text-gray-600 mb-4">{isEnglish ? 'No profile found' : 'Aucun profil trouvé'}</p>
          <Button onClick={() => navigate('/')}>{isEnglish ? 'Get started' : 'Commencer'}</Button>
        </div>
      </div>
    );
  }

  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || (isEnglish ? 'Your Name' : 'Votre Nom');
  const persona = personalityResult?.persona;
  const bigFiveScores = personalityResult?.big_five_scores;

  // Validate bigFiveScores - ensure all traits have valid numeric values
  const validBigFiveScores = bigFiveScores && typeof bigFiveScores === 'object' ? {
    openness: typeof bigFiveScores.openness === 'number' ? bigFiveScores.openness : 50,
    conscientiousness: typeof bigFiveScores.conscientiousness === 'number' ? bigFiveScores.conscientiousness : 50,
    extraversion: typeof bigFiveScores.extraversion === 'number' ? bigFiveScores.extraversion : 50,
    agreeableness: typeof bigFiveScores.agreeableness === 'number' ? bigFiveScores.agreeableness : 50,
    neuroticism: typeof bigFiveScores.neuroticism === 'number' ? bigFiveScores.neuroticism : 50,
  } : null;

  // Helper function to get persona name safely
  const getPersonaName = () => {
    if (!persona) return isEnglish ? 'Professional' : 'Professionnel';
    
    // Try different property names
    if (isEnglish) {
      return persona.name_en || persona.nameEn || persona.name || 'Professional';
    } else {
      return persona.name_fr || persona.nameFr || persona.name || 'Professionnel';
    }
  };

  // Helper function to get persona description safely
  const getPersonaDescription = () => {
    if (!persona) return '';
    
    if (isEnglish) {
      return persona.cv_blurb_en || persona.cvBlurbEn || persona.description_en || persona.descriptionEn || persona.description || '';
    } else {
      return persona.cv_blurb_fr || persona.cvBlurbFr || persona.description_fr || persona.descriptionFr || persona.description || '';
    }
  };

  // Helper function to get persona strengths safely
  const getPersonaStrengths = () => {
    if (!persona) return [];
    
    if (isEnglish) {
      return persona.strengths_en || persona.strengthsEn || persona.strengths || [];
    } else {
      return persona.strengths_fr || persona.strengthsFr || persona.strengths || [];
    }
  };

  // Trait descriptions based on score (qualitative, no percentages)
  const getTraitDescription = (trait, score) => {
    // Handle undefined or null scores
    if (score === undefined || score === null) {
      return isEnglish ? 'Balanced' : 'Équilibré';
    }
    
    const adjustedScore = trait === 'neuroticism' ? 100 - score : score;
    const descriptions = isEnglish ? {
      openness: {
        high: 'Creative & Curious',
        mid: 'Balanced & Adaptable',
        low: 'Practical & Focused'
      },
      conscientiousness: {
        high: 'Organized & Reliable',
        mid: 'Flexible & Balanced',
        low: 'Spontaneous & Adaptable'
      },
      extraversion: {
        high: 'Outgoing & Energetic',
        mid: 'Versatile & Balanced',
        low: 'Reflective & Independent'
      },
      agreeableness: {
        high: 'Collaborative & Supportive',
        mid: 'Balanced & Fair',
        low: 'Direct & Assertive'
      },
      neuroticism: {
        high: 'Calm & Resilient',
        mid: 'Balanced & Aware',
        low: 'Sensitive & Empathetic'
      }
    } : {
      openness: {
        high: 'Créatif & Curieux',
        mid: 'Équilibré & Adaptable',
        low: 'Pratique & Concentré'
      },
      conscientiousness: {
        high: 'Organisé & Fiable',
        mid: 'Flexible & Équilibré',
        low: 'Spontané & Adaptable'
      },
      extraversion: {
        high: 'Sociable & Énergique',
        mid: 'Polyvalent & Équilibré',
        low: 'Réfléchi & Indépendant'
      },
      agreeableness: {
        high: 'Collaboratif & Soutenant',
        mid: 'Équilibré & Juste',
        low: 'Direct & Affirmé'
      },
      neuroticism: {
        high: 'Calme & Résilient',
        mid: 'Équilibré & Conscient',
        low: 'Sensible & Empathique'
      }
    };
    
    if (adjustedScore >= 65) return descriptions[trait]?.high || '';
    if (adjustedScore >= 35) return descriptions[trait]?.mid || '';
    return descriptions[trait]?.low || '';
  };

  const bigFiveLabels = isEnglish ? {
    openness: 'Innovation',
    conscientiousness: 'Organization',
    extraversion: 'Communication',
    agreeableness: 'Teamwork',
    neuroticism: 'Resilience'
  } : {
    openness: 'Innovation',
    conscientiousness: 'Organisation',
    extraversion: 'Communication',
    agreeableness: 'Travail d\'équipe',
    neuroticism: 'Résilience'
  };

  const getPersonaColor = () => {
    if (!persona) return '#5BA946';
    const colors = { blue: '#3B82F6', purple: '#8B5CF6', green: '#5BA946', orange: '#F59E0B' };
    return colors[persona.color] || '#5BA946';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
      <Header />
      
      <main className="relative max-w-4xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-6 bg-white/70 backdrop-blur-xl rounded-2xl p-3 border border-gray-200 shadow-sm">
          <Button variant="outline" onClick={() => navigate(-1)} className="gap-2 w-full sm:w-auto">
            <ArrowLeft className="w-4 h-4" />
            {isEnglish ? 'Back' : 'Retour'}
          </Button>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Button 
              onClick={handleDownloadPDF} 
              disabled={downloading} 
              className="gap-2 bg-[#5BA946] hover:bg-[#4a9339] w-full sm:w-auto"
            >
              <Download className="w-4 h-4" />
              {downloading ? (isEnglish ? 'Generating...' : 'Génération...') : (isEnglish ? 'Download PDF' : 'Télécharger PDF')}
            </Button>
            <OpenAppButton 
              profileId={profile?.id}
              className="w-full sm:w-auto"
              variant="header"
            />
            <Button onClick={shareOnLinkedIn} variant="outline" className="gap-2 border-blue-400 text-blue-600 w-full sm:w-auto">
              <Linkedin className="w-4 h-4" />
              {isEnglish ? 'Share' : 'Partager'}
            </Button>
          </div>
        </div>

        {/* Complete Document */}
        <Card className="shadow-lg overflow-hidden bg-white rounded-2xl">
          <div ref={documentRef} className="bg-white p-6 sm:p-8">
            
            {/* ==================== CV SECTION FIRST ==================== */}
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mb-6 pb-6 border-b border-gray-200">
              <div className="w-20 h-20 flex-shrink-0">
                {profile.profile_picture ? (
                  <img src={profile.profile_picture} alt="Photo" className="w-full h-full rounded-xl object-cover border-2 border-[#5BA946]" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#5BA946] to-emerald-400 rounded-xl flex items-center justify-center text-white text-2xl font-bold">
                    {(profile.first_name?.[0] || '?')}{profile.last_name?.[0] || ''}
                  </div>
                )}
              </div>
              
              <div className="flex-1 text-center sm:text-left">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">{fullName}</h1>
                <p className="text-lg text-[#5BA946] font-semibold mb-2">{profile.target_job || (isEnglish ? 'Position' : 'Poste recherché')}</p>
                
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start text-sm text-gray-600">
                  {(profile.contact?.email || profile.email) && (
                    <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                      <Mail className="w-3 h-3" />
                      {profile.contact?.email || profile.email}
                    </span>
                  )}
                  {profile.contact?.phone && (
                    <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                      <Phone className="w-3 h-3" />
                      {profile.contact.phone}
                    </span>
                  )}
                  {profile.contact?.address && (
                    <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                      <MapPin className="w-3 h-3" />
                      {profile.contact.address}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Summary */}
            {profile.bio && (
              <section className="mb-6">
                <h2 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-[#5BA946]" />
                  {isEnglish ? 'Professional Summary' : 'Profil Professionnel'}
                </h2>
                <p className="text-gray-700 text-sm leading-relaxed">{profile.bio}</p>
              </section>
            )}

            {/* Experience */}
            {profile.experience?.length > 0 && (
              <section className="mb-6">
                <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#5BA946]" />
                  {isEnglish ? 'Experience' : 'Expérience'}
                </h2>
                <div className="space-y-3">
                  {profile.experience.map((exp, index) => (
                    <div key={exp.id || index} className="border-l-2 border-[#5BA946] pl-3">
                      <div className="flex flex-col sm:flex-row sm:justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{exp.position}</h3>
                          <p className="text-[#5BA946] text-sm">{exp.company}</p>
                        </div>
                        <span className="text-xs text-gray-500">{exp.start_date} - {exp.end_date || (isEnglish ? 'Present' : 'Présent')}</span>
                      </div>
                      {exp.description && <p className="mt-1 text-gray-600 text-xs">{exp.description}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Education */}
            {profile.education?.length > 0 && (
              <section className="mb-6">
                <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-[#5BA946]" />
                  {isEnglish ? 'Education' : 'Formation'}
                </h2>
                <div className="space-y-2">
                  {profile.education.map((edu, index) => (
                    <div key={edu.id || index} className="border-l-2 border-gray-300 pl-3">
                      <h3 className="font-semibold text-gray-900 text-sm">{edu.degree} {edu.field_of_study && `- ${edu.field_of_study}`}</h3>
                      <p className="text-gray-600 text-sm">{edu.institution}</p>
                      {edu.end_date && <span className="text-xs text-gray-500">{edu.end_date}</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Skills - Categorized as Hard and Soft */}
            {profile.skills?.length > 0 && (
              <section className="mb-6">
                <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Code className="w-4 h-4 text-[#5BA946]" />
                  {isEnglish ? 'Skills' : 'Compétences'}
                </h2>
                {(() => {
                  // Categorize skills
                  const softSkillKeywords = [
                    'leadership', 'communication', 'travail d\'équipe', 'esprit d\'équipe', 'adaptabilité',
                    'créativité', 'résolution de problèmes', 'gestion du temps', 'organisation',
                    'autonomie', 'rigueur', 'curiosité', 'empathie', 'négociation', 'présentation',
                    'collaboration', 'motivation', 'flexibilité', 'initiative', 'écoute',
                    'pédagogie', 'mentoring', 'coaching', 'gestion de projet', 'management',
                    'innovation', 'entrepreneuriat', 'conseil', 'stratégique', 'analyse',
                    'rédaction', 'blogging', 'networking', 'relationnel',
                    'teamwork', 'problem solving', 'time management', 'critical thinking',
                    'decision making', 'interpersonal', 'emotional intelligence', 'conflict resolution',
                    'public speaking', 'mentorship', 'strategic thinking'
                  ];
                  
                  const hardSkills = [];
                  const softSkills = [];
                  
                  (profile.skills || []).forEach(skill => {
                    const skillLower = skill.toLowerCase();
                    const isSoftSkill = softSkillKeywords.some(keyword => 
                      skillLower.includes(keyword.toLowerCase())
                    );
                    if (isSoftSkill) {
                      softSkills.push(skill);
                    } else {
                      hardSkills.push(skill);
                    }
                  });
                  
                  const displayHardSkills = hardSkills.slice(0, 10);
                  const displaySoftSkills = softSkills.slice(0, 10);
                  
                  return (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {/* Hard Skills */}
                      <div className="bg-blue-50 rounded-lg p-3">
                        <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-1">
                          <Code className="w-3 h-3" />
                          Hard Skills {hardSkills.length > 0 && <span className="text-xs text-blue-500">({Math.min(10, hardSkills.length)}/{hardSkills.length})</span>}
                        </h3>
                        <div className="flex flex-wrap gap-1">
                          {displayHardSkills.map((skill, index) => (
                            <Badge key={index} className="bg-blue-100 text-blue-800 text-xs">{skill}</Badge>
                          ))}
                          {hardSkills.length === 0 && (
                            <span className="text-blue-400 text-xs italic">{isEnglish ? 'None' : 'Aucune'}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Soft Skills */}
                      <div className="bg-purple-50 rounded-lg p-3">
                        <h3 className="text-sm font-semibold text-purple-800 mb-2 flex items-center gap-1">
                          <Brain className="w-3 h-3" />
                          Soft Skills {softSkills.length > 0 && <span className="text-xs text-purple-500">({Math.min(10, softSkills.length)}/{softSkills.length})</span>}
                        </h3>
                        <div className="flex flex-wrap gap-1">
                          {displaySoftSkills.map((skill, index) => (
                            <Badge key={index} className="bg-purple-100 text-purple-800 text-xs">{skill}</Badge>
                          ))}
                          {softSkills.length === 0 && (
                            <span className="text-purple-400 text-xs italic">{isEnglish ? 'None' : 'Aucune'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </section>
            )}

            {/* Languages */}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {profile.languages?.length > 0 && (
                <section>
                  <h2 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <LanguagesIcon className="w-4 h-4 text-[#5BA946]" />
                    {isEnglish ? 'Languages' : 'Langues'}
                  </h2>
                  <div className="space-y-1">
                    {profile.languages.map((lang, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-800">{lang.name}</span>
                        <span className="text-gray-500">{lang.level}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Certifications */}
            {profile.certifications?.length > 0 && (
              <section className="mb-6">
                <h2 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#5BA946]" />
                  {isEnglish ? 'Certifications' : 'Certifications'}
                </h2>
                <div className="space-y-1">
                  {profile.certifications.map((cert, index) => (
                    <div key={cert.id || index} className="flex justify-between text-sm">
                      <span className="text-gray-800">{cert.name}</span>
                      <span className="text-gray-500">{cert.issuer}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ==================== PERSONALITY SECTION AFTER CV ==================== */}
            
            {personalityResult && (
              <>
                <div className="border-t-2 border-dashed border-gray-300 my-8 pt-8">
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
                      <Brain className="w-5 h-5" style={{ color: getPersonaColor() }} />
                      {isEnglish ? 'Personality Assessment' : 'Évaluation de Personnalité'}
                    </h2>
                    <p className="text-gray-500 text-sm">
                      {isEnglish ? 'Soft Power Profile' : 'Profil Soft Power'}
                    </p>
                  </div>

                  {/* Persona Badge */}
                  <div className="text-center mb-6">
                    <div 
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold"
                      style={{ backgroundColor: getPersonaColor() }}
                    >
                      <span className="text-xl">{persona?.icon || '🎯'}</span>
                      <span>{getPersonaName()}</span>
                    </div>
                  </div>

                  {/* Persona Description */}
                  {persona && getPersonaDescription() && (
                    <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: `${getPersonaColor()}10` }}>
                      <p className="text-gray-700 text-sm text-center italic">
                        &ldquo;{getPersonaDescription()}&rdquo;
                      </p>
                    </div>
                  )}

                  {/* Professional Competencies - No percentages, just labels */}
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 text-center">
                      {isEnglish ? 'Professional Competencies' : 'Compétences Professionnelles'}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {validBigFiveScores && Object.entries(validBigFiveScores).map(([trait, score]) => (
                        <div key={trait} className="bg-gray-50 rounded-lg p-3 text-center">
                          <div className="text-xs font-semibold text-gray-600 mb-1">{bigFiveLabels[trait] || trait}</div>
                          <div className="text-sm font-medium" style={{ color: getPersonaColor() }}>
                            {getTraitDescription(trait, score)}
                          </div>
                        </div>
                      ))}
                      {!validBigFiveScores && (
                        <div className="col-span-5 text-center text-gray-400 text-sm py-4">
                          {isEnglish ? 'No competency data available' : 'Aucune donnée de compétences disponible'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Key Strengths */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-2 text-center">
                      {isEnglish ? 'Key Strengths' : 'Points Forts'}
                    </h3>
                    <div className="flex flex-wrap justify-center gap-2">
                      {getPersonaStrengths().length > 0 ? (
                        getPersonaStrengths().slice(0, 4).map((strength, idx) => (
                          <span 
                            key={idx} 
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white border"
                            style={{ borderColor: `${getPersonaColor()}40` }}
                          >
                            <CheckCircle className="w-3 h-3" style={{ color: getPersonaColor() }} />
                            {strength}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">
                          {isEnglish ? 'Complete the personality test to see your strengths' : 'Complétez le test de personnalité pour voir vos points forts'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Footer */}
            <div className="mt-8 pt-4 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-400">
                {isEnglish ? 'Created with EdoMatch' : 'Créé avec EdoMatch'} • {new Date().toLocaleDateString()}
              </p>
            </div>

          </div>
        </Card>

        {/* CTA if no personality test */}
        {!personalityResult && (
          <div className="mt-6 bg-purple-50 border border-purple-200 rounded-xl p-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-base font-bold text-gray-900 mb-1">
                  {isEnglish ? 'Complete your profile!' : 'Complétez votre profil !'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {isEnglish 
                    ? 'Take our 5-minute personality test to add your Soft Power profile.'
                    : 'Passez notre test de personnalité de 5 minutes pour ajouter votre profil Soft Power.'}
                </p>
              </div>
              <Button onClick={() => navigate('/personality-test')} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
                <Brain className="w-4 h-4 mr-2" />
                {isEnglish ? 'Take the test' : 'Passer le test'}
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Download Gate Modal */}
      <DownloadGateModal
        isOpen={showDownloadGate}
        onClose={closeDownloadGate}
        onSuccess={onLeadCaptured}
        profileId={profile?.id}
        firstName={profile?.first_name}
        downloadType={downloadType}
        language={isEnglish ? 'en' : 'fr'}
      />
    </div>
  );
}
